import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import { Plus, Trash2, ExternalLink, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function AdminWallets() {
    const [addrs, setAddrs] = useState([]);
    const [overview, setOverview] = useState(null);
    const [sweepables, setSweepables] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ currency: "USDT", address: "", network: "TRC-20", note: "", enabled: true });

    const loadAddresses = async () => {
        try {
            const { data } = await api.get("/admin/crypto-addresses");
            setAddrs(data);
        } catch {
            /* ignore */
        }
    };

    const loadOps = async () => {
        try {
            const [o, b] = await Promise.all([
                api.get("/admin/wallet-ops/overview"),
                api.get("/admin/sweep/balances"),
            ]);
            setOverview(o.data);
            setSweepables(b.data || []);
        } catch {
            /* ignore */
        }
    };

    useEffect(() => {
        loadAddresses();
        loadOps();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/crypto-addresses", form);
            toast.success("Adres eklendi");
            setOpen(false);
            setForm({ currency: "USDT", address: "", network: "TRC-20", note: "", enabled: true });
            loadAddresses();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        }
    };

    const update = async (id, payload) => {
        try {
            await api.patch(`/admin/crypto-addresses/${id}`, payload);
            toast.success("Güncellendi");
            loadAddresses();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        }
    };

    const remove = async (id) => {
        if (!confirm("Delete address?")) return;
        try {
            await api.delete(`/admin/crypto-addresses/${id}`);
            toast.success("Silindi");
            loadAddresses();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
        }
    };

    const totalSweepable = sweepables.reduce((s, x) => s + (x.balance || 0), 0);

    return (
        <div data-testid="admin-wallets" className="space-y-6">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="font-display font-bold text-3xl tracking-tight">Cüzdan Operasyonları</h1>
                    <p className="text-muted-foreground mt-1">
                        Bakiyeleri, gas gereksinimlerini ve çekim hedeflerini tek yerden yönetin.
                    </p>
                </div>
                <Link to="/admin/sweep" data-testid="wallets-open-sweep-center">
                    <Button variant="outline" className="border-white/10">Çekim Merkezi</Button>
                </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-4" data-testid="wallets-overview-cards">
                <div className="bg-card border border-white/[0.07] rounded-2xl p-5">
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Süpürülebilir toplam</p>
                    <p className="font-display text-3xl mt-2" data-testid="wallets-total-sweepable">{totalSweepable.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground mt-1">USDT/USDC kaynak bakiyesi</p>
                </div>

                <div className="bg-card border border-white/[0.07] rounded-2xl p-5">
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Gas otomasyonu</p>
                    <p className="text-sm mt-3" data-testid="wallets-gas-eth-status">ETH gas: {overview?.gas_automation?.eth_enabled ? "Açık" : "Kapalı"}</p>
                    <p className="text-sm mt-1" data-testid="wallets-gas-tron-status">TRON gas: {overview?.gas_automation?.tron_enabled ? "Açık" : "Kapalı"}</p>
                </div>

                <div className="bg-card border border-white/[0.07] rounded-2xl p-5">
                    <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">Para nereye çekiliyor?</p>
                    <ul className="text-xs font-mono mt-2 space-y-1 break-all" data-testid="wallets-cold-targets">
                        <li>TRC20: {overview?.cold_wallets?.USDT_TRC20 || "Tanımsız"}</li>
                        <li>USDT ERC20: {overview?.cold_wallets?.USDT_ERC20 || "Tanımsız"}</li>
                        <li>USDC ERC20: {overview?.cold_wallets?.USDC_ERC20 || "Tanımsız"}</li>
                    </ul>
                </div>
            </div>

            <div className="rounded-2xl border border-yellow-500/20 bg-yellow-500/[0.04] p-4 flex gap-3" data-testid="wallets-gas-notice">
                <ShieldAlert className="w-5 h-5 text-yellow-400 mt-0.5" />
                <div className="text-sm text-muted-foreground">
                    Sweep için kaynak adreste gas gerekir: ERC-20 için ETH, TRC-20 için TRX. Gas otomasyonu kapalıysa kaynak adresleri manuel fonlayın.
                </div>
            </div>

            <div className="bg-card border border-white/[0.07] rounded-2xl overflow-hidden" data-testid="wallets-sweepable-table">
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                    <h3 className="font-semibold">Canlı kaynak bakiyeler</h3>
                    <Link to="/admin/sweep" className="text-xs text-primary inline-flex items-center gap-1" data-testid="wallets-open-sweep-link">
                        Sweep ekranına git <ExternalLink className="w-3 h-3" />
                    </Link>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-white/[0.02] text-xs uppercase tracking-wide text-muted-foreground">
                            <tr>
                                <th className="text-left p-3">Kullanıcı</th>
                                <th className="text-left p-3">Para birimi</th>
                                <th className="text-right p-3">Bakiye</th>
                                <th className="text-left p-3">Kaynak adres</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {sweepables.map((r) => (
                                <tr key={`${r.user_id}-${r.currency}`}>
                                    <td className="p-3 font-mono text-xs">{r.user_id}</td>
                                    <td className="p-3">{r.currency}</td>
                                    <td className="p-3 text-right font-semibold">{Number(r.balance || 0).toFixed(2)}</td>
                                    <td className="p-3 font-mono text-xs break-all">{r.address}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {sweepables.length === 0 && (
                        <p className="text-sm text-muted-foreground p-8 text-center">Şu an süpürülebilir bakiye yok.</p>
                    )}
                </div>
            </div>

            <div className="flex items-end justify-between">
                <div>
                    <h2 className="font-display font-semibold text-2xl tracking-tight">Manuel Adresler</h2>
                    <p className="text-muted-foreground mt-1 text-sm">Kullanıcıya gösterilen legacy manuel adresleri yönetin.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="add-address-btn" className="bg-primary text-primary-foreground">
                            <Plus className="w-4 h-4 mr-2" /> Adres ekle
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-card border-white/10 max-w-md">
                        <DialogHeader>
                            <DialogTitle>Yeni adres ekle</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={create} className="space-y-3" data-testid="add-address-form">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>Currency</Label>
                                    <select value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} data-testid="addr-currency" className="w-full bg-background border border-white/10 rounded-lg px-3 py-2 text-sm">
                                        <option>USDT_TRC20</option><option>USDT_ERC20</option><option>USDC_ERC20</option>
                                    </select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label>Network</Label>
                                    <Input value={form.network} onChange={(e) => setForm((f) => ({ ...f, network: e.target.value }))} data-testid="addr-network" placeholder="TRC-20" className="bg-background border-white/10" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Address</Label>
                                <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="addr-address" placeholder="T..." className="bg-background border-white/10 font-mono" required />
                            </div>
                            <div className="space-y-1.5">
                                <Label>Note (shown to users)</Label>
                                <Textarea rows={2} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} data-testid="addr-note" className="bg-background border-white/10" />
                            </div>
                            <Button type="submit" data-testid="addr-submit" className="w-full bg-primary text-primary-foreground">Adresi kaydet</Button>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
                {addrs.map((a) => (
                    <div key={a.id} data-testid={`addr-row-${a.currency}`} className={`bg-card border rounded-2xl p-5 ${a.enabled ? "border-white/[0.08]" : "border-white/[0.03] opacity-60"}`}>
                        <div className="flex items-start justify-between mb-3">
                            <div>
                                <p className="font-display font-bold text-xl">{a.currency}</p>
                                {a.network && <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{a.network}</p>}
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1.5 text-xs">
                                    <input type="checkbox" checked={a.enabled} onChange={(e) => update(a.id, { enabled: e.target.checked })} data-testid={`addr-toggle-${a.id}`} />
                                    aktif
                                </label>
                                <button onClick={() => remove(a.id)} className="text-red-400 hover:text-red-300" data-testid={`addr-delete-${a.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <div className="font-mono text-xs bg-background border border-white/10 rounded-lg p-3 break-all mb-3">{a.address}</div>
                        {a.note && <p className="text-xs text-muted-foreground">{a.note}</p>}
                    </div>
                ))}
            </div>

            {addrs.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-12">Henüz adres yok. Kabul ettiğiniz her para birimi için en az bir adres ekleyin.</p>
            )}
        </div>
    );
}
